import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Avatar,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as PaidIcon,
  Cancel as UnpaidIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Groups as BatchIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  AddCard as RecordPaymentIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const BatchFeeStatus = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [feeData, setFeeData] = useState({ batches: [], month: null, year: null });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = unpaid, 1 = paid
  const [recordPaymentDialog, setRecordPaymentDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [manualPayment, setManualPayment] = useState({ amount: '', payment_method: 'cash', notes: '' });
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    years.push(y);
  }

  useEffect(() => {
    fetchFeeStatus();
  }, [selectedMonth, selectedYear]);

  const fetchFeeStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/batches/fee-status', {
        params: { month: selectedMonth, year: selectedYear },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeData(response.data);
    } catch (error) {
      toast.error('Failed to load fee status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (batchId) => (event, isExpanded) => {
    setExpandedBatch(isExpanded ? batchId : null);
    setTabValue(0); // Reset to unpaid tab
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const getMonthName = (monthNum) => {
    return months.find(m => m.value === monthNum)?.label || '';
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/export/batch-fee-report', {
        params: { month: selectedMonth, year: selectedYear },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_fee_report_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download report');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const openRecordPayment = (student) => {
    setSelectedStudent(student);
    setManualPayment({ amount: student.effective_fee || student.pending_amount || '', payment_method: 'cash', notes: '' });
    setRecordPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!manualPayment.amount || parseFloat(manualPayment.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setRecordingPayment(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/admin/students/${selectedStudent.id}/record-payment`, {
        month: selectedMonth,
        year: selectedYear,
        amount: parseFloat(manualPayment.amount),
        payment_method: manualPayment.payment_method,
        notes: manualPayment.notes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Payment of ₹${manualPayment.amount} recorded for ${selectedStudent.name}`);
      setRecordPaymentDialog(false);
      fetchFeeStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  // Calculate totals
  const totalStudents = feeData.batches.reduce((sum, b) => sum + b.total_students, 0);
  const totalPaid = feeData.batches.reduce((sum, b) => sum + b.paid_count, 0);
  const totalUnpaid = feeData.batches.reduce((sum, b) => sum + b.unpaid_count, 0);
  const overallRate = totalStudents > 0 ? Math.round((totalPaid / totalStudents) * 100) : 0;

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', pb: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 3, gap: 2 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: '#2C2416' }}>
              Batch Fee Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track fee payments batch-wise for each month
            </Typography>
          </Box>
          
          {/* Month/Year Selector */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={downloading}
              sx={{ bgcolor: '#D4AF37', '&:hover': { bgcolor: '#B8941F' }, whiteSpace: 'nowrap' }}
            >
              {isMobile ? 'Export' : 'Download Excel'}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: '#E3F2FD', border: '1px solid #BBDEFB' }}>
                  <CardContent sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#1976D2' }}>
                      {totalStudents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Students</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: '#E8F5E9', border: '1px solid #C8E6C9' }}>
                  <CardContent sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#4CAF50' }}>
                      {totalPaid}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Paid</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: '#FFF3E0', border: '1px solid #FFE0B2' }}>
                  <CardContent sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#FF9800' }}>
                      {totalUnpaid}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Unpaid</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: '#F3E5F5', border: '1px solid #E1BEE7' }}>
                  <CardContent sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#9C27B0' }}>
                      {overallRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Collection Rate</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Batch Accordions */}
            {feeData.batches.length === 0 ? (
              <Alert severity="info">No active batches found</Alert>
            ) : (
              feeData.batches.map((batch) => (
                <Accordion 
                  key={batch.batch_id}
                  expanded={expandedBatch === batch.batch_id}
                  onChange={handleAccordionChange(batch.batch_id)}
                  sx={{ 
                    mb: 2, 
                    borderRadius: '12px !important',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    '&:before': { display: 'none' },
                    overflow: 'hidden'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      bgcolor: '#FFFBF0',
                      '&:hover': { bgcolor: '#FFF8E1' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#D4AF37', width: 40, height: 40 }}>
                        <BatchIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{batch.batch_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {batch.total_students} students • {formatCurrency(batch.monthly_fee)}/month
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          icon={<PaidIcon sx={{ fontSize: 16 }} />}
                          label={`${batch.paid_count} Paid`}
                          size="small"
                          sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }}
                        />
                        <Chip 
                          icon={<UnpaidIcon sx={{ fontSize: 16 }} />}
                          label={`${batch.unpaid_count} Unpaid`}
                          size="small"
                          sx={{ bgcolor: '#FFEBEE', color: '#C62828' }}
                        />
                        <Box sx={{ width: 60, display: { xs: 'none', sm: 'block' } }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={batch.collection_rate} 
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#FFEBEE',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: batch.collection_rate >= 80 ? '#4CAF50' : batch.collection_rate >= 50 ? '#FF9800' : '#F44336'
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: 10 }}>{batch.collection_rate}%</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={(e, v) => setTabValue(v)}
                      sx={{ 
                        bgcolor: '#f5f5f5',
                        '& .MuiTab-root': { fontWeight: 600 }
                      }}
                    >
                      <Tab 
                        label={`Unpaid (${batch.unpaid_count})`}
                        icon={<WarningIcon sx={{ color: '#FF9800', fontSize: 18 }} />}
                        iconPosition="start"
                      />
                      <Tab 
                        label={`Paid (${batch.paid_count})`}
                        icon={<PaidIcon sx={{ color: '#4CAF50', fontSize: 18 }} />}
                        iconPosition="start"
                      />
                    </Tabs>
                    
                    {/* Unpaid Students */}
                    {tabValue === 0 && (
                      <Box sx={{ p: 2 }}>
                        {batch.unpaid_students.length === 0 ? (
                          <Alert severity="success" icon={<PaidIcon />}>
                            All students have paid for {getMonthName(selectedMonth)} {selectedYear}! 🎉
                          </Alert>
                        ) : (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#FFEBEE' }}>
                                  <TableCell><strong>Student Name</strong></TableCell>
                                  {!isMobile && <TableCell><strong>Parent</strong></TableCell>}
                                  <TableCell><strong>Contact</strong></TableCell>
                                  <TableCell align="right"><strong>Due Amount</strong></TableCell>
                                  <TableCell align="center"><strong>Action</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {batch.unpaid_students.map((student) => (
                                  <TableRow key={student.id} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>{student.name}</Typography>
                                      {isMobile && (
                                        <Typography variant="caption" color="text.secondary">{student.parent_name}</Typography>
                                      )}
                                    </TableCell>
                                    {!isMobile && <TableCell>{student.parent_name}</TableCell>}
                                    <TableCell>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title={student.phone}>
                                          <IconButton
                                            size="small"
                                            href={`tel:${student.phone}`}
                                            sx={{ bgcolor: '#E8F5E9', '&:hover': { bgcolor: '#C8E6C9' } }}
                                          >
                                            <PhoneIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                          </IconButton>
                                        </Tooltip>
                                        {student.email && (
                                          <Tooltip title={student.email}>
                                            <IconButton
                                              size="small"
                                              href={`mailto:${student.email}`}
                                              sx={{ bgcolor: '#E3F2FD', '&:hover': { bgcolor: '#BBDEFB' } }}
                                            >
                                              <EmailIcon sx={{ fontSize: 16, color: '#1976D2' }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={formatCurrency(student.pending_amount || student.effective_fee)}
                                        size="small"
                                        sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 700 }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Record manual payment">
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={<RecordPaymentIcon sx={{ fontSize: 16 }} />}
                                          onClick={() => openRecordPayment(student)}
                                          sx={{
                                            bgcolor: '#2E7D32',
                                            '&:hover': { bgcolor: '#1B5E20' },
                                            fontSize: '0.7rem',
                                            py: 0.5,
                                            px: 1,
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {isMobile ? '' : 'Record Payment'}
                                        </Button>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    )}
                    
                    {/* Paid Students */}
                    {tabValue === 1 && (
                      <Box sx={{ p: 2 }}>
                        {batch.paid_students.length === 0 ? (
                          <Alert severity="warning">
                            No payments received yet for {getMonthName(selectedMonth)} {selectedYear}
                          </Alert>
                        ) : (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#E8F5E9' }}>
                                  <TableCell><strong>Student Name</strong></TableCell>
                                  {!isMobile && <TableCell><strong>Parent</strong></TableCell>}
                                  <TableCell><strong>Contact</strong></TableCell>
                                  <TableCell align="right"><strong>Amount Paid</strong></TableCell>
                                  {!isMobile && <TableCell><strong>Payment Date</strong></TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {batch.paid_students.map((student) => (
                                  <TableRow key={student.id} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>{student.name}</Typography>
                                      {isMobile && (
                                        <>
                                          <Typography variant="caption" color="text.secondary" display="block">{student.parent_name}</Typography>
                                          {student.payment_date && (
                                            <Typography variant="caption" color="success.main">
                                              Paid: {new Date(student.payment_date).toLocaleDateString()}
                                            </Typography>
                                          )}
                                        </>
                                      )}
                                    </TableCell>
                                    {!isMobile && <TableCell>{student.parent_name}</TableCell>}
                                    <TableCell>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title={student.phone}>
                                          <IconButton 
                                            size="small" 
                                            href={`tel:${student.phone}`}
                                            sx={{ bgcolor: '#E8F5E9', '&:hover': { bgcolor: '#C8E6C9' } }}
                                          >
                                            <PhoneIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                          </IconButton>
                                        </Tooltip>
                                        {student.email && (
                                          <Tooltip title={student.email}>
                                            <IconButton 
                                              size="small"
                                              href={`mailto:${student.email}`}
                                              sx={{ bgcolor: '#E3F2FD', '&:hover': { bgcolor: '#BBDEFB' } }}
                                            >
                                              <EmailIcon sx={{ fontSize: 16, color: '#1976D2' }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip 
                                        icon={<PaidIcon sx={{ fontSize: 14 }} />}
                                        label={formatCurrency(student.amount_paid)}
                                        size="small"
                                        sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700 }}
                                      />
                                    </TableCell>
                                    {!isMobile && (
                                      <TableCell>
                                        {student.payment_date ? new Date(student.payment_date).toLocaleDateString() : '-'}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </>
        )}
      </Box>

      {/* Record Manual Payment Dialog */}
      <Dialog open={recordPaymentDialog} onClose={() => setRecordPaymentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2E7D32', color: 'white', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RecordPaymentIcon />
            Record Manual Payment
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedStudent && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Student
              </Typography>
              <Typography variant="body1" fontWeight={700} gutterBottom>
                {selectedStudent.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {getMonthName(selectedMonth)} {selectedYear}
              </Typography>
              <TextField
                fullWidth
                label="Amount Paid"
                type="number"
                value={manualPayment.amount}
                onChange={(e) => setManualPayment(p => ({ ...p, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={manualPayment.payment_method}
                onChange={(e) => setManualPayment(p => ({ ...p, payment_method: e.target.value }))}
                sx={{ mb: 2 }}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="net_banking">Net Banking</MenuItem>
                <MenuItem value="debit_card">Debit Card</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Notes (optional)"
                placeholder="e.g. Collected by coordinator"
                value={manualPayment.notes}
                onChange={(e) => setManualPayment(p => ({ ...p, notes: e.target.value }))}
                multiline
                rows={2}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setRecordPaymentDialog(false)} disabled={recordingPayment}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRecordPayment}
            disabled={recordingPayment || !manualPayment.amount}
            sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}
          >
            {recordingPayment ? <CircularProgress size={20} color="inherit" /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default BatchFeeStatus;
